import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../utils/firebase';
import { useAuth } from '../contexts/authContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { v4 as uuidv4 } from 'uuid';


const AdminAds = () => {
  const { user, loading, isAdmin, ready } = useAuth();
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [venues, setVenues] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    gameInfo: true,
    pregameAds: true,
    halftimeGameInfo: true,
    halftimeAds: true,
    tiebreakerGameInfo: true,
    tiebreakerAds: true,
    finalGameInfo: true,
    finalAds: true
  });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    slideType: 'ad', // ad, rules, promo, league
    stage: 'pregame', // pregame, halftime, final
    category: 'ads', // ads, gameInfo
    targetType: 'global', // global, venue, city, state, country, chain
    targetValue: '',
    imageUrl: '',
    duration: 10, // seconds
    active: true,
    priority: 1,
    order: 1 // for ordering within each section
  });

  useEffect(() => {
    // Wait for auth to be ready
    if (!ready) return;
    
    // Only redirect if we're sure the user is not an admin (use comprehensive check)
    if (!loading && user && !isAdmin) {
      console.log('❌ AdminAds: User is not admin, redirecting');
      navigate('/not-authorized');
      return;
    }
    
    // Only fetch content if user is admin
    if (user && isAdmin) {
      fetchSlides();
      fetchVenues();
    }
  }, [user, isAdmin, loading, ready, navigate]);

  const fetchSlides = async () => {
    try {
      const slidesQuery = query(collection(db, 'slides'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(slidesQuery);
      const slidesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSlides(slidesData);
    } catch (error) {
      console.error('Error fetching slides:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      const venuesData = venuesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVenues(venuesData);
    } catch (error) {
      console.log('Venues collection does not exist yet or access denied. This is normal for new installations.');
      // Set empty venues array - admins can still create slides without venues
      setVenues([]);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return '';
    
    if (!user) {
      throw new Error('You must be logged in to upload images');
    }
    
    try {
      setUploading(true);
      const fileExtension = file.name.split('.').pop();
      const fileName = `slide-images/${uuidv4()}.${fileExtension}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error.code === 'storage/unauthorized') {
        throw new Error('Upload failed: Unauthorized. Please check your Firebase Storage permissions.');
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error('Upload failed: Storage quota exceeded.');
      } else {
        throw new Error(`Failed to upload image: ${error.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Admin users bypass all moderation - they have full control
      const moderationResult = {
        status: 'approved',
        score: 0,
        bypassed: true,
        reason: 'Admin user - moderation bypassed'
      };

      const slideData = {
        ...formData,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user.uid,
        moderationStatus: moderationResult?.status || 'pending',
        moderationScore: moderationResult?.score || 0
      };

      if (editingSlide) {
        await updateDoc(doc(db, 'slides', editingSlide.id), {
          ...slideData,
          updatedAt: new Date()
        });
        console.log('Slide updated successfully');
      } else {
        await addDoc(collection(db, 'slides'), slideData);
        console.log('Slide created successfully');
      }

      // Reset form
      setFormData({
        title: '',
        content: '',
        slideType: 'ad',
        stage: 'pregame',
        category: 'ads',
        targetType: 'global',
        targetValue: '',
        imageUrl: '',
        duration: 10,
        active: true,
        priority: 1,
        order: 1
      });
      setShowCreateForm(false);
      setEditingSlide(null);
      
      // Refresh slides
      await fetchSlides();
      
    } catch (error) {
      console.error('Error saving slide:', error);
      alert('Error saving slide: ' + error.message);
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      title: slide.title || '',
      content: slide.content || '',
      slideType: slide.slideType || 'ad',
      stage: slide.stage || 'pregame',
      category: slide.category || 'ads',
      targetType: slide.targetType || 'global',
      targetValue: slide.targetValue || '',
      imageUrl: slide.imageUrl || '',
      duration: slide.duration || 10,
      active: slide.active !== false,
      priority: slide.priority || 1,
      order: slide.order || 1
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (slideId) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        await deleteDoc(doc(db, 'slides', slideId));
        await fetchSlides();
      } catch (error) {
        console.error('Error deleting slide:', error);
        alert('Error deleting slide: ' + error.message);
      }
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const imageUrl = await handleImageUpload(file);
        setFormData(prev => ({ ...prev, imageUrl }));
      } catch (error) {
        alert('Error uploading image: ' + error.message);
      }
    }
  };

  const getSlideTypeIcon = (type) => {
    switch (type) {
      case 'ad': return '📢';
      case 'rules': return '📋';
      case 'promo': return '🎁';
      case 'league': return '🏆';
      default: return '📄';
    }
  };

  const getTargetTypeLabel = (type) => {
    switch (type) {
      case 'global': return 'Global';
      case 'venue': return 'Specific Venue';
      case 'city': return 'City';
      case 'state': return 'State';
      case 'country': return 'Country';
      case 'chain': return 'Chain';
      default: return type;
    }
  };



  const getCategoryLabel = (category) => {
    switch (category) {
      case 'ads': return 'Ads';
      case 'gameInfo': return 'Game Info';
      default: return category;
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getSlidesForSection = (stage, category) => {
    return slides.filter(slide => slide.stage === stage && slide.category === category)
      .sort((a, b) => a.order - b.order);
  };

  const renderSection = (stage, category, sectionKey, title, description, icon) => {
    const sectionSlides = getSlidesForSection(stage, category);
    
    return (
      <div key={sectionKey} className="bg-white rounded-lg shadow-xl overflow-hidden mb-3">
        <div 
          className="px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-xl">{icon}</span>
              <div>
                <h3 className="text-md font-semibold text-gray-800">{title}</h3>
                <p className="text-xs text-gray-500">{description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">{sectionSlides.length} slides</span>
              <button className="text-gray-400 hover:text-gray-600">
                {expandedSections[sectionKey] ? '▼' : '▶'}
              </button>
            </div>
          </div>
        </div>
        
        {expandedSections[sectionKey] && (
          <div className="p-3">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-gray-700">Slides in this section</h4>
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, stage, category }));
                  setShowCreateForm(true);
                }}
                className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors text-xs"
              >
                ➕ Add {getCategoryLabel(category)} Slide
              </button>
            </div>
            
            {sectionSlides.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <div className="text-2xl mb-1">{icon}</div>
                <p className="text-sm">No {getCategoryLabel(category).toLowerCase()} slides yet.</p>
                <p className="text-xs">Click "Add {getCategoryLabel(category)} Slide" to create one.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sectionSlides.map((slide) => (
                  <div key={slide.id} className="border border-gray-200 rounded-lg p-2 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-lg">{getSlideTypeIcon(slide.slideType)}</span>
                          <h5 className="text-sm font-semibold text-gray-800">{slide.title}</h5>
                          <span className={`px-1 py-0.5 rounded-full text-xs font-medium ${
                            slide.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {slide.active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-1 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            Order: {slide.order}
                          </span>
                          <span className="px-1 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            Priority: {slide.priority}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 mb-2 text-xs line-clamp-2">{slide.content}</p>
                        
                        <div className="flex items-center space-x-3 text-xs text-gray-500">
                          <span>🎯 {getTargetTypeLabel(slide.targetType)}</span>
                          {slide.targetValue && <span>📍 {slide.targetValue}</span>}
                          <span>⏱️ {slide.duration}s</span>
                          <span>📅 {slide.createdAt?.toDate?.() ? 
                            slide.createdAt.toDate().toLocaleDateString() : 
                            'Recently'
                          }</span>
                        </div>

                        {slide.imageUrl && (
                          <div className="mt-2">
                            <img
                              src={slide.imageUrl}
                              alt="Slide"
                              className="max-h-16 rounded border"
                            />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-1 ml-2">
                        <button
                          onClick={() => handleEdit(slide)}
                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(slide.id)}
                          className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loadingContent) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading slides...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">📢 Admin Ads & Slides</h1>
            <p className="text-gray-300 text-sm">Organized slide management for different game stages</p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105 text-sm"
          >
            ➕ Create New Slide
          </button>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-xl p-4 mb-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {editingSlide ? 'Edit Slide' : 'Create New Slide'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slide Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter slide title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slide Type
                  </label>
                  <select
                    value={formData.slideType}
                    onChange={(e) => setFormData(prev => ({ ...prev, slideType: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="ad">📢 Advertisement</option>
                    <option value="rules">📋 Game Rules</option>
                    <option value="promo">🎁 Promotion</option>
                    <option value="league">🏆 League Info</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Game Stage *
                  </label>
                  <select
                    value={formData.stage}
                    onChange={(e) => setFormData(prev => ({ ...prev, stage: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  >
                    <option value="pregame">🎮 Pregame</option>
                    <option value="halftime">⏸️ Halftime</option>
                    <option value="tiebreaker">⚡ Tiebreaker</option>
                    <option value="final">🏁 Final</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  >
                    <option value="gameInfo">📋 Game Info</option>
                    <option value="ads">📢 Ads</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order *
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) }))}
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="1, 2, 3..."
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter slide content..."
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Type
                  </label>
                  <select
                    value={formData.targetType}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetType: e.target.value, targetValue: '' }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="global">🌍 Global (All Venues)</option>
                    <option value="venue">🏢 Specific Venue</option>
                    <option value="city">🏙️ City</option>
                    <option value="state">🗺️ State</option>
                    <option value="country">🌎 Country</option>
                    <option value="chain">🏪 Chain</option>
                  </select>
                </div>

                {formData.targetType !== 'global' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Value
                    </label>
                    {formData.targetType === 'venue' ? (
                      <select
                        value={formData.targetValue}
                        onChange={(e) => setFormData(prev => ({ ...prev, targetValue: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">Select a venue</option>
                        {venues.map(venue => (
                          <option key={venue.id} value={venue.id}>
                            {venue.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={formData.targetValue}
                        onChange={(e) => setFormData(prev => ({ ...prev, targetValue: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder={`Enter ${formData.targetType}`}
                      />
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Duration (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                    min="5"
                    max="60"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority (1-10)
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                    min="1"
                    max="10"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slide Image (Optional)
                </label>
                <input
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {uploading && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
              </div>

              {formData.imageUrl && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Image
                  </label>
                  <img
                    src={formData.imageUrl}
                    alt="Slide preview"
                    className="max-h-32 rounded-lg border"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingSlide(null);
                    setFormData({
                      title: '',
                      content: '',
                      slideType: 'ad',
                      stage: 'pregame',
                      category: 'ads',
                      targetType: 'global',
                      targetValue: '',
                      imageUrl: '',
                      duration: 10,
                      active: true,
                      priority: 1,
                      order: 1
                    });
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {editingSlide ? 'Update Slide' : 'Create Slide'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Organized Sections */}
        <div className="space-y-3">
          {/* Game Info Sections */}
          {renderSection('pregame', 'gameInfo', 'gameInfo', '🎮 Game Info (Pregame)', 'Game rules and league information shown at the beginning', '📋')}
          {renderSection('halftime', 'gameInfo', 'halftimeGameInfo', '⏸️ Game Info (Halftime)', 'Game information shown during halftime', '📋')}
          {renderSection('tiebreaker', 'gameInfo', 'tiebreakerGameInfo', '⚡ Game Info (Tiebreaker)', 'Tiebreaker rules and instructions shown when needed', '📋')}
          {renderSection('final', 'gameInfo', 'finalGameInfo', '🏁 Game Info (Final)', 'Game information shown at the end', '📋')}
          
          {/* Ads Sections */}
          {renderSection('pregame', 'ads', 'pregameAds', '📢 Pregame Ads', 'Advertisements shown before the game starts', '📢')}
          {renderSection('halftime', 'ads', 'halftimeAds', '⏸️ Halftime Ads', 'Advertisements shown during halftime', '📢')}
          {renderSection('tiebreaker', 'ads', 'tiebreakerAds', '⚡ Tiebreaker Ads', 'Advertisements shown during tiebreaker rounds', '📢')}
          {renderSection('final', 'ads', 'finalAds', '🏁 Final Ads', 'Advertisements shown at the end of the game', '📢')}
        </div>
      </div>
    </div>
  );
};

export default AdminAds; 